import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Receipt, Info, ExternalLink, Landmark, ShieldCheck } from "lucide-react";
import SEO from "../components/SEO";
import InvoiceForm from "../components/facturation/InvoiceForm";
import InvoiceList from "../components/facturation/InvoiceList";
import { API } from "../apiBase";

const YEAR = new Date().getFullYear();

function MandatCard({ mandat, setMandat }) {
  const [v, setV] = useState(mandat);
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(mandat), [mandat]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/pro/facturation/mandat`, v);
      setMandat(r.data);
      toast.success("Mandat expert-comptable enregistré.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full h-9 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[13px] text-[#0F2B5B] outline-none focus:border-[#0EA5E9]";
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="mandat-card">
      <div className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-[#0F2B5B]">
        <Landmark size={15} className="text-[#0284C7]" /> Mandat expert-comptable
      </div>
      <p className="mt-1 text-[11.5px] text-[#475569] leading-relaxed">
        Autorisez la transmission automatique de vos factures Factur-X à votre cabinet comptable
        (guide CNOEC mars 2026 — mandat de délégation).
      </p>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-[0.14em] text-[#475569] mb-1">Cabinet / expert-comptable</span>
          <input value={v.accountant_name || ""} onChange={(e) => setV((p) => ({ ...p, accountant_name: e.target.value }))}
                 data-testid="mandat-name" className={inputCls} />
        </label>
        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-[0.14em] text-[#475569] mb-1">Email de transmission</span>
          <input value={v.accountant_email || ""} onChange={(e) => setV((p) => ({ ...p, accountant_email: e.target.value }))}
                 type="email" data-testid="mandat-email" className={inputCls} />
        </label>
      </div>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="mandat-auto-label">
          <input type="checkbox" checked={!!v.auto_transmit}
                 onChange={(e) => setV((p) => ({ ...p, auto_transmit: e.target.checked }))}
                 data-testid="mandat-auto" className="h-4 w-4 accent-[#0F2B5B]" />
          <span className="text-[12.5px] text-[#0F2B5B]">Transmission automatique à chaque émission</span>
        </label>
        <button onClick={save} disabled={busy} data-testid="mandat-save"
                className="h-9 px-5 rounded-full bg-[#0F2B5B] text-white text-[12.5px] font-medium disabled:opacity-50">
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

export default function MssanteFacturation() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [mandat, setMandat] = useState({ accountant_name: "", accountant_email: "", auto_transmit: false });

  const refreshSummary = async () => {
    try {
      const r = await axios.get(`${API}/pro/facturation/summary?year=${YEAR}`);
      setSummary(r.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [i, m] = await Promise.all([
          axios.get(`${API}/pro/facturation/invoices?year=${YEAR}`),
          axios.get(`${API}/pro/facturation/mandat`),
        ]);
        setInvoices(i.data || []);
        setMandat(m.data);
        refreshSummary();
      } catch (e) {
        console.error("facturation load:", e);
        toast.error("Chargement du module facturation impossible.");
      }
    })();
  }, []);

  const kpis = summary ? [
    ["Factures émises", summary.count, ""],
    ["CA exonéré TVA (261-4)", `${summary.exonere_total.toLocaleString("fr-FR")} €`, "nomenclature"],
    ["CA hors nomenclature HT", `${summary.tva_ht_total.toLocaleString("fr-FR")} €`, `${summary.tva_count} factures Factur-X`],
    ["TVA collectée", `${summary.tva_collectee.toLocaleString("fr-FR")} €`, "e-reporting"],
  ] : [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="page-facturation">
      <SEO title="Facturation électronique & comptable" noIndex />
      <section className="pt-28 pb-6">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Link to="/mssante/espace" data-testid="fact-back"
                className="text-[11px] uppercase tracking-[0.18em] text-[#0284C7] hover:text-[#0F2B5B] inline-flex items-center gap-1 mb-3">
            <ArrowLeft size={12} /> Retour à mon espace
          </Link>
          <h1 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            Facturation électronique &amp; comptable
          </h1>
          <p className="mt-2 text-[13.5px] text-[#475569] max-w-3xl leading-relaxed">
            Factures au format Factur-X (EN 16931), distinction actes exonérés / soumis à TVA,
            e-reporting et transmission automatisée à votre expert-comptable.
          </p>

          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#BAE6FD] bg-[#F0F9FF] p-4 flex items-start gap-3" data-testid="fact-reglement">
              <Info size={16} className="text-[#0284C7] mt-0.5 shrink-0" />
              <div className="text-[12px] text-[#0F2B5B] leading-relaxed">
                <b>Échéance 1er sept. 2026</b> : réception des factures électroniques obligatoire pour
                tous les assujettis TVA. Les actes de nomenclature restent <b>exonérés (art. 261-4-1° CGI)</b> ;
                seuls vos actes <b>hors nomenclature</b> sont soumis à Factur-X + e-reporting.
                Sanction : 500 € par transmission manquante (plafond 15 000 €/an).
              </div>
            </div>
            <div className="rounded-xl border border-[#A7F3D0] bg-[#ECFDF5] p-4 flex items-start gap-3" data-testid="fact-isca">
              <ShieldCheck size={16} className="text-[#059669] mt-0.5 shrink-0" />
              <div className="text-[12px] text-[#0F2B5B] leading-relaxed">
                <b>Conformité MetaConnect</b> : critères ISCA (Inaltérabilité, Sécurisation, Conservation,
                Archivage), XML CII EN 16931 embarqué dans chaque PDF, journal de transmissions
                horodaté à valeur probante.
              </div>
            </div>
          </div>
        </div>
      </section>

      {summary && (
        <section className="pb-6">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="fact-kpis">
            {kpis.map(([label, val, sub]) => (
              <div key={label} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
                <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#475569]">{label}</div>
                <div className="mt-1 font-display text-[#0F2B5B] text-2xl">{val}</div>
                {sub && <div className="text-[10.5px] text-[#94A3B8] mt-0.5">{sub}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="pb-6">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_380px] gap-4 items-start">
          <InvoiceForm onCreated={(inv) => { setInvoices((p) => [inv, ...p]); refreshSummary(); }} />
          <MandatCard mandat={mandat} setMandat={setMandat} />
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <InvoiceList invoices={invoices} setInvoices={setInvoices}
                       mandatEmail={mandat.accountant_email} year={YEAR} />
          <p className="mt-3 text-[11px] text-[#94A3B8]">
            Références :{" "}
            <a href="https://bofip.impots.gouv.fr" target="_blank" rel="noreferrer" className="underline text-[#0284C7] inline-flex items-center gap-0.5">BOFiP <ExternalLink size={9} /></a>{" · "}
            guide CNOEC mars 2026 · avenant 18 (SESAM sans Vitale / dégradé).
          </p>
        </div>
      </section>
    </div>
  );
}
